<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity" @click.self="closeModal">
      <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl transform transition-all">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold flex items-center">
            <FolderPlusIcon v-if="!isEditMode" class="h-8 w-8 text-blue-600 mr-3" aria-hidden="true" />
            <PencilIcon v-else class="h-8 w-8 text-blue-600 mr-3" aria-hidden="true" />
            {{ title }}
          </h2>
          <button class="text-gray-500 hover:text-gray-700" @click="closeModal">
            <XIcon class="h-6 w-6" />
          </button>
        </div>

        <div class="space-y-4">
          <div>
            <label for="collectionName" class="block text-sm font-medium text-gray-700">Nom de la collection</label>
            <input
              id="collectionName"
              v-model="collectionName"
              type="text"
              name="collectionName"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Ex: Projets de recherche 2024"
              @keydown.enter="handleSubmit"
            />
          </div>
          <div>
            <label for="collectionDescription" class="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              id="collectionDescription"
              v-model="collectionDescription"
              name="collectionDescription"
              rows="3"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Décrivez l'objectif ou le contenu de la collection..."
            />
          </div>
          <div>
            <label class="flex items-center">
              <input
                v-model="isPublic"
                type="checkbox"
                class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              <span class="ml-2 text-sm font-medium text-gray-700">Collection publique</span>
            </label>
            <p class="mt-1 text-xs text-gray-500">Les collections publiques sont visibles par tous les utilisateurs</p>
          </div>
        </div>
        
        <div class="flex justify-end mt-8">
            <button
              type="button"
              class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              @click="closeModal"
            >
              Annuler
            </button>
            <button
              type="button"
              class="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
              :disabled="!collectionName.trim()"
              @click="handleSubmit"
            >
              {{ submitButtonText }}
            </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Modal de confirmation -->
  <ModalAreYouSure
    v-model="showConfirmModal"
    title="Modifications non sauvegardées"
    message="Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir fermer sans sauvegarder ?"
    confirm-text="Fermer sans sauvegarder"
    cancel-text="Continuer l'édition"
    @confirm="confirmClose"
    @cancel="cancelClose"
  />
</template>

<script setup lang="ts">
import type { MemoCollection, MemoCollectionCreate } from '@/api/types/memos/collections';
import { useAuthStore } from '@/stores/auth';
import { useCollectionsStore } from '@/stores/collections';
import { FolderPlusIcon, PencilIcon, XIcon } from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toastification';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  collectionToEdit: {
    type: Object as PropType<MemoCollection | null>,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue', 'collection-saved']);

const collectionsStore = useCollectionsStore();
const authStore = useAuthStore();
const toast = useToast();

const collectionName = ref('');
const collectionDescription = ref('');
const isPublic = ref(false);
const showConfirmModal = ref(false);
const initialDataSnapshot = ref<{ name: string; description: string; is_public: boolean } | null>(null);

const isEditMode = computed(() => !!props.collectionToEdit);
const title = computed(() => isEditMode.value ? 'Modifier la collection' : 'Créer une nouvelle collection');
const submitButtonText = computed(() => isEditMode.value ? 'Enregistrer' : 'Créer');

// Fonction pour vérifier s'il y a des modifications
const hasModifications = (): boolean => {
  if (!initialDataSnapshot.value) {
    // En mode création, vérifier si des champs ont été remplis
    return !!(collectionName.value.trim() || collectionDescription.value.trim());
  }
  
  // En mode édition, comparer avec les données initiales
  const snapshot = initialDataSnapshot.value;
  return collectionName.value !== snapshot.name ||
         collectionDescription.value !== snapshot.description ||
         isPublic.value !== snapshot.is_public;
};

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    collectionName.value = props.collectionToEdit?.name || '';
    collectionDescription.value = props.collectionToEdit?.description || '';
    isPublic.value = props.collectionToEdit?.is_public ?? false;
    
    // Sauvegarder un snapshot des données initiales pour la comparaison
    initialDataSnapshot.value = {
      name: collectionName.value,
      description: collectionDescription.value,
      is_public: isPublic.value
    };
  } else {
    // Réinitialiser le snapshot quand le modal se ferme
    initialDataSnapshot.value = null;
  }
});

const closeModal = (): void => {
  if (hasModifications()) {
    showConfirmModal.value = true;
  } else {
    resetForm();
    emit('update:modelValue', false);
  }
};

const confirmClose = (): void => {
  showConfirmModal.value = false;
  resetForm();
  emit('update:modelValue', false);
};

const cancelClose = (): void => {
  showConfirmModal.value = false;
};

const resetForm = (): void => {
  collectionName.value = '';
  collectionDescription.value = '';
  isPublic.value = false;
  initialDataSnapshot.value = null;
};

const handleSubmit = async (): Promise<void> => {
  if (!collectionName.value.trim()) {
    toast.error('Le nom de la collection ne peut pas être vide.');
    return;
  }
  if (!authStore.user?.uuid) {
    toast.error('Utilisateur non identifié. Impossible de créer la collection.');
    return;
  }

  let success = false;
  let resultCollection: MemoCollection | null = null;
  
  if (isEditMode.value && props.collectionToEdit) {
    const updatedCollection = await collectionsStore.updateCollection(
      props.collectionToEdit.uuid,
      { name: collectionName.value, description: collectionDescription.value, is_public: isPublic.value }
    );
    success = !!updatedCollection;
    resultCollection = updatedCollection;
    if (success) {
      toast.success('Collection mise à jour.');
    }
  } else {
    const collectionData: MemoCollectionCreate = {
      name: collectionName.value,
      description: collectionDescription.value,
      user_id: authStore.user.uuid,
      is_public: isPublic.value,
    };
    const newCollection = await collectionsStore.createCollection(collectionData);
    success = !!newCollection;
    resultCollection = newCollection;
    if (success) {
      toast.success('Collection créée.');
    }
  }

  if (success && resultCollection) {
    emit('collection-saved', resultCollection);
    resetForm();
    emit('update:modelValue', false);
  } else {
    toast.error(collectionsStore.getError || 'Une erreur est survenue.');
  }
};
</script> 